// Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box, Typography, Paper, Stack, Button, Chip,
    Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuthContext } from "@asgardeo/auth-react";
import { Store } from 'react-notifications-component';
import { ProjectDetail, ProjectHealthStatus, RiskActionItem, ActionItemComment } from '../data/utils/types';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import CircularLoading from './Loading';

const PRIORITY_BORDER: Record<string, string> = {
    high: '#d32f2f',
    medium: '#ed6c02',
    low: '#1976d2',
};

const STATUS_LABELS: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    cancelled: 'Cancelled',
};

function getPriorityLabel(priority: string): string {
    const p = priority.toUpperCase();
    return p === 'MEDIUM' ? 'MED' : p;
}

function getPriorityChipColor(priority: string): 'error' | 'warning' | 'info' | 'default' {
    switch (priority.toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'default';
    }
}

function getStatusChipColor(status: string): 'warning' | 'info' | 'success' | 'default' {
    switch (status) {
        case 'open': return 'warning';
        case 'in_progress': return 'info';
        case 'resolved': return 'success';
        case 'cancelled': return 'default';
        default: return 'default';
    }
}

function DueDateDisplay({ dueDate, status }: { dueDate: string; status: string }) {
    const [y, m, d] = dueDate.split('-').map(Number);
    const date = new Date(y, m - 1, d); // parse as local date to avoid UTC shift
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isOverdue = date < today && (status === 'open' || status === 'in_progress');
    return isOverdue ? (
        <Typography variant="caption" sx={{ color: 'error.main' }}>
            {date.toLocaleDateString()} (Overdue)
        </Typography>
    ) : (
        <Typography variant="caption" color="text.secondary">
            {date.toLocaleDateString()}
        </Typography>
    );
}

interface ProjectWithRisk {
    sysId: string;
    name: string;
    riskId: number;
}

interface ActionItemsSectionProps {
    accountId: string;
    projects: ProjectDetail[];
    healthStatusData: ProjectHealthStatus[];
    onDataChanged?: () => void;
    openAddForProject?: string | null;
    onAddForProjectConsumed?: () => void;
}

interface AddActionItemDialogProps {
    open: boolean;
    onClose: () => void;
    accountId: string;
    projectsWithOpenRisks: ProjectWithRisk[];
    onCreated: () => void;
    defaultProjectSysId?: string;
}

const AddActionItemDialog: React.FC<AddActionItemDialogProps> = ({
    open, onClose, accountId, projectsWithOpenRisks, onCreated, defaultProjectSysId
}) => {
    const { httpRequest, getAccessToken } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [selectedProjectSysId, setSelectedProjectSysId] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('medium');
    const [assignedTo, setAssignedTo] = useState('');
    const [dueDate, setDueDate] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedProjectSysId(defaultProjectSysId ?? projectsWithOpenRisks[0]?.sysId ?? '');
            setTitle('');
            setDescription('');
            setPriority('medium');
            setAssignedTo('');
            setDueDate('');
        }
    }, [open]); 

    const selectedProject = projectsWithOpenRisks.find(p => p.sysId === selectedProjectSysId);
    const canCreate = !!title.trim() && !!selectedProject && !!priority && !!dueDate;

    const handleCreate = async () => {
        if (!canCreate) return;
        setLoading(true);
        try {
            const token = await getAccessToken();
            await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/risks/${selectedProject!.riskId}/action-items`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-jwt-assertion': token },
                data: {
                    title: title.trim(),
                    description: description.trim() || null,
                    priority,
                    assignedToEmail: assignedTo.trim() || null,
                    dueDate: dueDate,
                    projectSysId: selectedProject!.sysId,
                    accountSysId: accountId,
                }
            });
            Store.addNotification({
                title: "Created!", message: "Action item created successfully.",
                type: "success", insert: "top", container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000, onScreen: true }
            });
            onCreated();
        } catch (err: any) {
            Store.addNotification({
                title: "Failed!", message: "Failed to create action item.",
                type: "danger", insert: "top", container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000, onScreen: true }
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <CircularLoading openLoading={loading} />
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Action Item</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Project</InputLabel>
                            <Select
                                value={selectedProjectSysId}
                                label="Project"
                                onChange={e => setSelectedProjectSysId(e.target.value as string)}
                            >
                                {projectsWithOpenRisks.map(p => (
                                    <MenuItem key={p.sysId} value={p.sysId}>{p.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth required label="Title"
                            value={title} onChange={e => setTitle(e.target.value)}
                        />
                        <TextField
                            fullWidth label="Description" multiline rows={3}
                            value={description} onChange={e => setDescription(e.target.value)}
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={priority}
                                label="Priority"
                                onChange={e => setPriority(e.target.value as string)}
                            >
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth label="Assigned To (Email)"
                            value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
                        />
                        <TextField
                            fullWidth required label="Due Date" type="date"
                            value={dueDate} onChange={e => setDueDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={handleCreate}
                        disabled={!canCreate}
                        sx={{ color: '#e96900' }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

interface InlineCommentThreadProps {
    itemId: number;
    onCommentPosted: (itemId: number) => void;
}

const InlineCommentThread: React.FC<InlineCommentThreadProps> = ({ itemId, onCommentPosted }) => {
    const { httpRequest, getAccessToken } = useAuthContext();
    const [comments, setComments] = useState<ActionItemComment[]>([]);
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [posting, setPosting] = useState(false);
    const commentsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchComments();
    }, [itemId]); 

    useEffect(() => {
        commentsEndRef.current?.scrollIntoView({ block: 'end' });
    }, [comments]);

    const fetchComments = async () => {
        setLoading(true);
        try {
            const token = await getAccessToken();
            const response = await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/action-items/${itemId}/comments`,
                method: 'GET',
                headers: { Accept: 'application/json', 'x-jwt-assertion': token },
            });
            setComments(response.data as ActionItemComment[]);
        } catch {
            setComments([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePost = async () => {
        if (!newComment.trim() || posting) return;
        setPosting(true);
        try {
            const token = await getAccessToken();
            const response = await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/action-items/${itemId}/comments`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'x-jwt-assertion': token },
                data: { comment: newComment.trim() },
            });
            setComments(prev => [...prev, response.data as ActionItemComment]);
            setNewComment('');
            onCommentPosted(itemId);
        } catch {
            Store.addNotification({
                title: 'Failed!', message: 'Failed to post comment.',
                type: 'danger', insert: 'top', container: 'top-right',
                animationIn: ['animate__animated', 'animate__fadeIn'],
                animationOut: ['animate__animated', 'animate__fadeOut'],
                dismiss: { duration: 4000, onScreen: true },
            });
        } finally {
            setPosting(false);
        }
    };

    const formatCommentDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) +
            ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    return (
        <Box sx={{ borderTop: '1px solid #f0f0f0' }}>
            <Box sx={{ maxHeight: 300, overflowY: 'auto', px: 2, py: 1 }}>
                {loading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        Loading comments...
                    </Typography>
                ) : comments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No comments yet. Be the first to comment.
                    </Typography>
                ) : (
                    <>
                        {comments.map((c, idx) => (
                            <Box key={c.id} sx={{
                                py: 1.25,
                                borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none',
                            }}>
                                <Stack direction="row" alignItems="baseline" spacing={1}>
                                    <Typography variant="body2" fontWeight={700}>{c.createdByEmail}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatCommentDate(c.createdOn)}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {c.comment}
                                </Typography>
                            </Box>
                        ))}
                        <div ref={commentsEndRef} />
                    </>
                )}
            </Box>
            <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    size="small"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); }
                    }}
                />
                <Button
                    variant="contained"
                    disabled={!newComment.trim() || posting}
                    onClick={handlePost}
                    sx={{
                        minWidth: 64, flexShrink: 0, bgcolor: '#e96900',
                        '&:hover': { bgcolor: '#c85a00' },
                    }}
                >
                    {posting ? '...' : 'Post'}
                </Button>
            </Box>
        </Box>
    );
};

const ActionItemsSection: React.FC<ActionItemsSectionProps> = ({
    accountId, projects, healthStatusData, onDataChanged, openAddForProject, onAddForProjectConsumed
}) => {
    const { httpRequest, getAccessToken } = useAuthContext();
    const [mutationLoading, setMutationLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [selectedProjectSysId, setSelectedProjectSysId] = useState('');

    // Add dialog
    const [addOpen, setAddOpen] = useState(false);
    const [addDefaultProject, setAddDefaultProject] = useState<string | undefined>(undefined);

    // Status change dialog (for resolved / cancelled)
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [pendingChange, setPendingChange] = useState<{ itemId: number; newStatus: string } | null>(null);
    const [statusComment, setStatusComment] = useState('');

    // Comment counts + expanded items
    const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const { data: actionItems, loading: itemsLoading, getApiData: fetchActionItems } =
        useGetApi<RiskActionItem[]>({
            url: RESOURCE_SERVER_URLS[0] + `/customer-health/accounts/${accountId}/action-items`
        });

    useEffect(() => {
        if (accountId) fetchActionItems();
    }, [accountId]); 

    useEffect(() => {
        if (openAddForProject) {
            setAddDefaultProject(openAddForProject);
            setAddOpen(true);
            onAddForProjectConsumed?.();
        }
    }, [openAddForProject]); 

    useEffect(() => {
        if (actionItems) {
            const counts: Record<string, number> = {};
            actionItems.forEach(item => { counts[item.id] = item.commentCount ?? 0; });
            setCommentCounts(counts);
        }
    }, [actionItems]);

    // Projects that currently have an open risk (for add dialog + project filter)
    const projectsWithOpenRisks = useMemo<ProjectWithRisk[]>(() => {
        return healthStatusData
            .filter(s => s.healthStatus.status === 'at_risk' && s.openRisk !== null)
            .map(s => ({
                sysId: s.projectSysId,
                name: projects.find(p => p.sysId === s.projectSysId)?.name ?? s.projectSysId,
                riskId: s.openRisk!.id,
            }));
    }, [healthStatusData, projects]);

    // Apply project filter
    const projectFilteredItems = useMemo(() => {
        if (!actionItems) return [];
        if (!selectedProjectSysId) return actionItems;
        return actionItems.filter(i => i.projectSysId === selectedProjectSysId);
    }, [actionItems, selectedProjectSysId]);

    // Tab counts (based on project-filtered items)
    const counts = useMemo(() => ({
        all: projectFilteredItems.length,
        open: projectFilteredItems.filter(i => i.status === 'open').length,
        in_progress: projectFilteredItems.filter(i => i.status === 'in_progress').length,
        resolved: projectFilteredItems.filter(i => i.status === 'resolved').length,
        cancelled: projectFilteredItems.filter(i => i.status === 'cancelled').length,
    }), [projectFilteredItems]);

    // Apply tab filter
    const displayedItems = useMemo(() => {
        if (activeTab === 'all') return projectFilteredItems;
        return projectFilteredItems.filter(i => i.status === activeTab);
    }, [projectFilteredItems, activeTab]);

    const refresh = () => {
        fetchActionItems();
        if (onDataChanged) onDataChanged();
    };

    const getProjectName = (projectSysId: string) =>
        projects.find(p => p.sysId === projectSysId)?.name ?? projectSysId;

    const handleCommentPosted = (itemId: number) => {
        setCommentCounts(prev => ({ ...prev, [itemId]: (prev[itemId] ?? 0) + 1 }));
    };

    const toggleExpanded = (itemId: number) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    };

    // Direct status update (open ↔ in_progress)
    const handleStatusChange = async (itemId: number, newStatus: string) => {
        if (newStatus === 'resolved' || newStatus === 'cancelled') {
            setPendingChange({ itemId, newStatus });
            setStatusComment('');
            setStatusDialogOpen(true);
            return;
        }
        setMutationLoading(true);
        try {
            const token = await getAccessToken();
            await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/action-items/${itemId}/status`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-jwt-assertion': token },
                data: { status: newStatus, resolutionComment: null }
            });
            Store.addNotification({
                title: "Updated!", message: "Status updated.",
                type: "success", insert: "top", container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000, onScreen: true }
            });
            refresh();
        } catch (err: any) {
            Store.addNotification({
                title: "Failed!", message: "Failed to update status.",
                type: "danger", insert: "top", container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000, onScreen: true }
            });
        } finally {
            setMutationLoading(false);
        }
    };

    // Confirm resolve / cancel with comment
    const handleStatusChangeConfirm = async () => {
        if (!pendingChange || !statusComment.trim()) return;
        setMutationLoading(true);
        try {
            const token = await getAccessToken();
            await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/action-items/${pendingChange.itemId}/status`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-jwt-assertion': token },
                data: { status: pendingChange.newStatus, resolutionComment: statusComment }
            });
            Store.addNotification({
                title: "Updated!", message: "Status updated.",
                type: "success", insert: "top", container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000, onScreen: true }
            });
            setStatusDialogOpen(false);
            setPendingChange(null);
            setStatusComment('');
            refresh();
        } catch (err: any) {
            Store.addNotification({
                title: "Failed!", message: "Failed to update status.",
                type: "danger", insert: "top", container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000, onScreen: true }
            });
        } finally {
            setMutationLoading(false);
        }
    };


    return (
        <Box id="action-items-section" sx={{ mt: 3 }}>
            <CircularLoading openLoading={mutationLoading} />

            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AssignmentIcon sx={{ color: '#e96900' }} />
                    <Typography variant="h6" fontWeight="bold">Risk Mitigation Action Items</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {counts.open} open · {counts.in_progress} in progress · {counts.resolved} resolved · {counts.cancelled} cancelled
                    </Typography>
                </Stack>
                <Tooltip title={projectsWithOpenRisks.length === 0 ? "Action items can only be created when there's an active risk" : ""}>
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setAddOpen(true)}
                            disabled={projectsWithOpenRisks.length === 0}
                            sx={{
                                color: '#e96900', borderColor: '#e96900',
                                ':hover': { bgcolor: '#fff3e0', borderColor: '#e96900' }
                            }}
                        >
                            Add Action Item
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {/* Tabs + Project filter */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    textColor="inherit"
                    sx={{
                        minHeight: 36,
                        '& .MuiTab-root': { minHeight: 36, py: 0.5, fontWeight: 600, fontSize: '0.75rem' }
                    }}
                >
                    <Tab label={`ALL (${counts.all})`} value="all" />
                    <Tab label={`OPEN (${counts.open})`} value="open" />
                    <Tab label={`IN PROGRESS (${counts.in_progress})`} value="in_progress" />
                    <Tab label={`RESOLVED (${counts.resolved})`} value="resolved" />
                    <Tab label={`CANCELLED (${counts.cancelled})`} value="cancelled" />
                </Tabs>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>All Projects</InputLabel>
                    <Select
                        value={selectedProjectSysId}
                        label="All Projects"
                        onChange={e => setSelectedProjectSysId(e.target.value as string)}
                    >
                        <MenuItem value="">All Projects</MenuItem>
                        {projectsWithOpenRisks.map(p => (
                            <MenuItem key={p.sysId} value={p.sysId}>{p.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* Loading / Empty states */}
            {itemsLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Loading...</Typography>
            )}
            {!itemsLoading && displayedItems.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No action items found.
                </Typography>
            )}

            {/* Action Item Cards */}
            <Stack spacing={1.5}>
                {displayedItems.map(item => {
                    const isMuted = item.status === 'resolved' || item.status === 'cancelled';
                    const borderColor = PRIORITY_BORDER[item.priority.toLowerCase()] ?? '#9e9e9e';

                    return (
                        <Paper
                            key={item.id}
                            elevation={1}
                            sx={{ borderLeft: `4px solid ${borderColor}`, overflow: 'hidden', opacity: isMuted ? 0.65 : 1 }}
                        >
                            <Box sx={{ p: 2 }}>
                                {/* Row 1: priority chip, title, project chip */}
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                    <Chip
                                        label={getPriorityLabel(item.priority)}
                                        color={getPriorityChipColor(item.priority)}
                                        size="small"
                                        sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                    />
                                    <Typography variant="body1" fontWeight="bold">{item.title}</Typography>
                                    <Chip
                                        label={getProjectName(item.projectSysId)}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </Stack>

                                {/* Description */}
                                {item.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {item.description}
                                    </Typography>
                                )}

                                {/* Resolution / cancellation comment */}
                                {item.resolutionComment && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, fontStyle: 'italic' }}
                                    >
                                        {item.status === 'cancelled' ? 'Cancelled: ' : 'Resolution: '}
                                        {item.resolutionComment}
                                    </Typography>
                                )}

                                {/* Row 2: assigned, due date, status */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    flexWrap="wrap"
                                    sx={{ mt: 1 }}
                                >
                                    {item.assignedToEmail && (
                                        <Typography variant="caption" color="text.secondary">
                                            Assigned: {item.assignedToEmail}
                                        </Typography>
                                    )}
                                    {item.dueDate && (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="caption" color="text.secondary">Due:</Typography>
                                            <DueDateDisplay dueDate={item.dueDate} status={item.status} />
                                        </Box>
                                    )}
                                    <Box sx={{ ml: 'auto' }}>
                                        {isMuted ? (
                                            <Chip
                                                label={STATUS_LABELS[item.status]}
                                                color={getStatusChipColor(item.status)}
                                                size="small"
                                            />
                                        ) : (
                                            <Select
                                                size="small"
                                                value={item.status}
                                                onChange={e => handleStatusChange(item.id, e.target.value as string)}
                                                sx={{ fontSize: '0.8rem', minWidth: 130 }}
                                            >
                                                <MenuItem value="open">Open</MenuItem>
                                                <MenuItem value="in_progress">In Progress</MenuItem>
                                                <MenuItem value="resolved">Resolved</MenuItem>
                                                <MenuItem value="cancelled">Cancelled</MenuItem>
                                            </Select>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Comments bar */}
                            <Box
                                onClick={() => toggleExpanded(item.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 0.75,
                                    backgroundColor: '#fafafa', borderTop: '1px solid #f0f0f0',
                                    cursor: 'pointer', userSelect: 'none',
                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                }}
                            >
                                <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Comments ({commentCounts[item.id] ?? item.commentCount ?? 0})
                                </Typography>
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                                    {expandedItems.has(item.id)
                                        ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    }
                                </Box>
                            </Box>

                            {/* Inline comment thread */}
                            {expandedItems.has(item.id) && (
                                <InlineCommentThread itemId={item.id} onCommentPosted={handleCommentPosted} />
                            )}
                        </Paper>
                    );
                })}
            </Stack>

            {/* Resolve / Cancel dialog */}
            <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {pendingChange?.newStatus === 'resolved' ? 'Resolve Action Item' : 'Cancel Action Item'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth required multiline rows={3}
                        placeholder={
                            pendingChange?.newStatus === 'resolved'
                                ? 'Add resolution comment...'
                                : 'Add cancellation reason...'
                        }
                        value={statusComment}
                        onChange={e => setStatusComment(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setStatusDialogOpen(false); setStatusComment(''); }}>Cancel</Button>
                    <Button onClick={handleStatusChangeConfirm} disabled={!statusComment.trim()}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Add Action Item dialog */}
            <AddActionItemDialog
                open={addOpen}
                onClose={() => { setAddOpen(false); setAddDefaultProject(undefined); }}
                accountId={accountId}
                projectsWithOpenRisks={projectsWithOpenRisks}
                onCreated={() => { setAddOpen(false); setAddDefaultProject(undefined); refresh(); }}
                defaultProjectSysId={addDefaultProject}
            />


        </Box>
    );
};

export default ActionItemsSection;
